import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { interval, Observable, of, Subject } from 'rxjs';
import { catchError, filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { DynamicOrderFormAppIds } from '../../marketplace-app/global-brands-constants';
import { ActivationService } from '../core/activation.service';
import { acceptedTLDs, DomainSearchResult, DynamicOrderFormService, parseDomain } from './dynamic-order-form.service';

import { UntypedFormBuilder, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { CustomFieldsAnswer } from '@vendasta/store';
import { ProductService } from '../../core/product.service';
import { Product } from '../../core/product/product';
import { ActivationNavigationService } from '../core/activation-navigation.service';
import { ProductOrderFormStoreService } from '../core/product-order-form.store.service';
import { TermsOfServiceDialogComponent } from './terms-of-service-dialog.component';

@Component({
  styleUrls: ['dynamic-order-form-page.component.scss'],
  templateUrl: './dynamic-order-form-page.component.html',
})
export class DynamicOrderFormPageComponent implements OnInit, OnDestroy {
  constructor(
    private activationService: ActivationService,
    private dynamicOrderFormService: DynamicOrderFormService,
    private productService: ProductService,
    private router: Router,
    private orderFormStoreService: ProductOrderFormStoreService,
    private snackbarService: SnackbarService,
    private activationNavigationService: ActivationNavigationService,
    private formBuilder: UntypedFormBuilder,
    public dialog: MatDialog,
  ) {}

  domainSearchResult$: Observable<DomainSearchResult>;
  domainSuggestionResults$: Observable<DomainSearchResult[]>;
  displaySuggestions: boolean;
  product: Observable<Product>;
  urlTracking: string;
  godaddyForm: UntypedFormGroup;
  private ngUnsubscribe$$ = new Subject<void>();

  @ViewChild('domainSearchText')
  domainSearchTextElement: ElementRef;

  ngOnInit(): void {
    this.godaddyForm = this.formBuilder.group({
      domain: [''],
      suggestedDomain: [''],
      tos: [false, Validators.requiredTrue],
    });
    this.godaddyForm.setValidators(this.InputProvidedValidation());

    // Remove radio selection on return to input box
    this.godaddyForm
      .get('domain')
      .valueChanges.pipe(takeUntil(this.ngUnsubscribe$$))
      .subscribe(() => {
        this.godaddyForm.get('suggestedDomain').setValue('');
        this.godaddyForm.get('domain').setErrors([{ invalidDomain: true }]);
      });

    this.product = this.activationService.selectedItems$.pipe(
      map((selectedItems) =>
        Array.from(selectedItems.keys()).find((item) => DynamicOrderFormAppIds.indexOf(item) !== -1),
      ),
      switchMap((appId) => this.productService.getProducts([appId])),
      filter((prods) => prods && prods.length > 0),
      map((prods) => prods[0]),
    );

    const selectSearchBoxSubscription = interval(100).subscribe(() => {
      if (this.domainSearchTextElement) {
        this.domainSearchTextElement.nativeElement.focus();
        selectSearchBoxSubscription.unsubscribe();
      }
    });

    this.domainSearchResult$ = of({
      domain: '',
      available: false,
      parseError: '',
    });

    this.urlTracking = this.router.url;
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$$.next();
    this.ngUnsubscribe$$.complete();
  }

  suggestionClicked(): void {
    // suggested domains are valid choices
    this.godaddyForm.get('domain').setErrors(null);
  }

  submitDomain(inputDomain: string): void {
    if (inputDomain === '') {
      return;
    }
    const parseDomainResult = parseDomain(inputDomain);
    if (parseDomainResult.parseError !== '') {
      this.domainSearchResult$ = of({
        domain: '',
        available: false,
        parseError: parseDomainResult.parseError,
      });
      return;
    }
    this.displaySuggestions = true;
    this.domainSearchResult$ = this.dynamicOrderFormService.checkDomainAvailability(parseDomainResult.domain).pipe(
      tap((result) => {
        if (!result.available) {
          this.godaddyForm.get('domain').setErrors([{ invalidDomain: true }]);
          this.domainSuggestionResults$ = this.dynamicOrderFormService.getDomainSuggestions(
            parseDomainResult.domain,
            6,
            acceptedTLDs,
          );
        } else {
          this.godaddyForm.get('domain').setValue(parseDomainResult.domain);
          this.godaddyForm.get('domain').setErrors(null);
        }
        return;
      }),
      catchError((err) => {
        this.snackbarService.errorSnack('Failed to look up domain. Please try again.');
        return of(err);
      }),
    );
  }

  nextStep(): void {
    this.cacheDynamicOrderFormData()
      .pipe(take(1))
      .subscribe((_) => {
        this.activationNavigationService.navigateToNextStep('dynamic-order-form');
      });
  }

  previousStep(): void {
    this.activationNavigationService.navigateToPreviousStep('dynamic-order-form');
  }

  // Either a radio button has been selected OR a domain has been input
  InputProvidedValidation(): ValidatorFn {
    return (): ValidationErrors => {
      return this.godaddyForm.get('suggestedDomain').value || this.godaddyForm.get('domain').value
        ? null
        : { noInput: true };
    };
  }

  openTermsOfServiceDialog(): void {
    this.dialog.open(TermsOfServiceDialogComponent, {
      width: '800px',
    });
  }

  private cacheDynamicOrderFormData(): Observable<boolean> {
    const ready = new Subject<boolean>();
    this.product.pipe(take(1)).subscribe((product) => {
      const chosenDomain = this.godaddyForm.get('suggestedDomain').value || this.godaddyForm.get('domain').value;

      const orderFormData: CustomFieldsAnswer[] = [{ fieldId: 'domain', answer: chosenDomain }];
      this.orderFormStoreService.cacheDynamicOrderForm(product.product_id, orderFormData);
      ready.next(true);
    });

    return ready.asObservable();
  }
}
